/**
 * BCR 制品包页面 — 独立路由，只读展示某个底软包最新 BCR 任务的制品包卡片内容。
 * 从「底软交付物」页操作列的「制品包」按钮跳转进入。
 * 页面会展示最新 BCR 任务的制品，并定时刷新，保证新触发的 BCR 产出后能自动更新。
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Spin, Typography, Tag } from 'antd';
import { ArrowLeftOutlined, InboxOutlined } from '@ant-design/icons';
import BcrArtifacts from '@/components/bcr/BcrArtifacts';
import * as bcrApi from '@/api/bcr';
import * as packageApi from '@/api/packages';
import type { BcrArtifactItem } from '@/types';

const { Title, Text } = Typography;

const POLL_INTERVAL_MS = 30000;

const STATUS_LABEL: Record<string, { text: string; color: string }> = {
  pending: { text: '排队中', color: 'gray' },
  running: { text: '运行中', color: 'orange' },
  finished: { text: '完成', color: 'green' },
  stop: { text: '终止', color: 'red' },
};

const BcrArtifactsPage = () => {
  const { projectId, packageId } = useParams<{ projectId: string; packageId: string }>();
  const navigate = useNavigate();
  const numProjectId = Number(projectId);
  const numPackageId = Number(packageId);

  const [packageName, setPackageName] = useState<string>('');
  const [customArtifacts, setCustomArtifacts] = useState<
    Array<{ kind: string; name: string; url?: string }>
  >([]);
  const [uploadedArtifacts, setUploadedArtifacts] = useState<Record<string, BcrArtifactItem>>();
  const [latestTaskId, setLatestTaskId] = useState<number | null>(null);
  const [latestTaskStatus, setLatestTaskStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const mountedRef = useRef(true);

  // 加载底软包信息（名称 + 包级别自定义制品），自定义制品不随 BCR 变化，仅加载一次
  useEffect(() => {
    if (!numPackageId) return;
    packageApi
      .getPackage(numPackageId)
      .then((detail) => {
        if (!mountedRef.current) return;
        setPackageName(detail.name);
        setCustomArtifacts(detail.custom_artifacts || []);
      })
      .catch(() => {
        // silent fail
      });
  }, [numPackageId]);

  // 加载最新 BCR 任务的制品（列表已按 -created_at 倒序，results[0] 即最新任务）
  const loadLatest = useCallback(async () => {
    try {
      const result = await bcrApi.listBcrTasks(numProjectId, {
        package_id: numPackageId,
        page: 1,
        page_size: 1,
      });
      if (!mountedRef.current) return;
      const latest = result.results[0];
      if (latest) {
        setLatestTaskId(latest.id);
        setLatestTaskStatus(latest.status);
        const detail = await bcrApi.getBcrTaskDetail(numProjectId, latest.id);
        if (!mountedRef.current) return;
        setUploadedArtifacts(detail.uploaded_artifacts);
      } else {
        setLatestTaskId(null);
        setLatestTaskStatus(null);
        setUploadedArtifacts(undefined);
      }
    } catch {
      // silent fail — 保留上一次数据
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [numProjectId, numPackageId]);

  // 首次加载 + 定时刷新，保证新触发 BCR 产出后自动更新
  useEffect(() => {
    mountedRef.current = true;
    loadLatest();
    const timer = setInterval(loadLatest, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(timer);
    };
  }, [loadLatest]);

  const handleClose = () => navigate(`/packages/${projectId}`);

  const statusCfg = latestTaskStatus ? STATUS_LABEL[latestTaskStatus] : null;

  return (
    <div className="p-4">
      <div className="flex items-center gap-4 mb-4">
        <Button icon={<ArrowLeftOutlined />} onClick={handleClose}>
          返回
        </Button>
        <Title level={4} className="!mb-0">
          <InboxOutlined className="mr-2" />
          制品包 - {packageName || `包#${packageId}`}
        </Title>
        {latestTaskId != null && (
          <Text type="secondary">
            最新 BCR 任务 #{latestTaskId}
            {statusCfg && (
              <Tag color={statusCfg.color} className="ml-2">
                {statusCfg.text}
              </Tag>
            )}
          </Text>
        )}
      </div>

      {loading ? (
        <Card>
          <div className="flex justify-center py-12">
            <Spin size="large" />
          </div>
        </Card>
      ) : (
        <BcrArtifacts
          uploadedArtifacts={uploadedArtifacts}
          customArtifacts={customArtifacts}
        />
      )}
    </div>
  );
};

export default BcrArtifactsPage;
